import { Typography, Box, UnorderedList, UnorderedListItem, Stack } from "@carrotfertility/carotene-core"
import { FormattedMessage, defineMessage } from "react-intl"
import React from "react"

export const rtxConsentHeaderMessage = defineMessage({
  defaultMessage: "Consent to share your data with AlightWell"
})

export const rtxConsentCheckboxLabel = defineMessage({
  defaultMessage: "Yes, I give my consent."
})

interface RtxConsentInnerContentProps {
  showHeader?: boolean
}

export function RtxConsentInnerContent({ showHeader = true }: RtxConsentInnerContentProps) {
  return (
    <Stack>
      {showHeader && (
        <Box marginBottom={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
          <Typography variant="h3" component="h3">
            <FormattedMessage {...rtxConsentHeaderMessage} />
          </Typography>
        </Box>
      )}
      <Box>
        <Typography>
          <FormattedMessage defaultMessage="To participate in the Healthy You incentives program, Carrot must share limited information about your activity on the Carrot platform with AlightWell, the program administrator. AlightWell will use this information only as needed to operate the rewards program." />
        </Typography>
      </Box>
      <Box marginTop={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
        <Typography fontWeight="bold">
          <FormattedMessage defaultMessage="What we collect" />
        </Typography>
        <Typography>
          <FormattedMessage
            defaultMessage="Carrot will automatically capture certain “eligible actions” you take on the platform—such as registering
                with Carrot, receiving a Carrot Plan, reading an article, or chatting with a Carrot Expert. These activity records (for example, that an action
                occurred and when) may indicate engagement with reproductive health or well-being resources, and may therefore be considered sensitive personal data."
          />
        </Typography>
      </Box>
      <Box marginTop={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
        <Typography fontWeight="bold">
          <FormattedMessage defaultMessage="How we use it" />
        </Typography>
        <Typography>
          <FormattedMessage defaultMessage="This information is used only to:" />
          <UnorderedList
            sx={{
              marginBlockStart: (theme) => theme.spacing(theme.tokens.spacing.sm),
              "li::marker": {
                color: (theme) => theme.palette.text.primary
              }
            }}
          >
            <UnorderedListItem>
              <FormattedMessage defaultMessage="Verify your eligibility and enrollment" />
            </UnorderedListItem>
            <UnorderedListItem>
              <FormattedMessage defaultMessage="Confirm completion of eligible activities and apply rewards" />
            </UnorderedListItem>
            <UnorderedListItem>
              <FormattedMessage defaultMessage="Operate, support, and audit the rewards integration with AlightWell" />
            </UnorderedListItem>
          </UnorderedList>
        </Typography>
      </Box>
      <Box marginTop={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
        <Typography fontWeight="bold">
          <FormattedMessage defaultMessage="Your choice" />
        </Typography>
        <Typography>
          <FormattedMessage defaultMessage="Providing consent is voluntary. You may withdraw your consent at any time, with effect going forward, by contacting info@get-carrot.com." />
        </Typography>
        <Typography marginTop={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
          <FormattedMessage defaultMessage="If you do not provide consent, you will not be able to participate in the Healthy You incentives program through Carrot." />
        </Typography>
        <Typography
          marginTop={(theme) => theme.spacing(theme.tokens.spacing.lg)}
          marginBottom={(theme) => theme.spacing(theme.tokens.spacing.xl)}
        >
          <FormattedMessage defaultMessage="By giving consent, you confirm that you've read and understood the explanation above." />
        </Typography>
      </Box>
    </Stack>
  )
}
